import { useEffect, useRef, useState } from 'react';

import { getIsPdfLocationAnchor, parseMarkdownLocationLink } from '@aline/common';
import { link, params } from '@aline/routes';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useScrollToLocationForReference } from 'src/editor';
import {
  openLink,
  useConsumePendingParam,
  useDispatchNotification,
  useDocumentIdFromUrl,
} from 'src/helpers';
import { useEditorLoading, useMaybeEditor } from 'src/layout-engine';

import {
  parseAlineLocation,
  parseFileLocation,
} from '../../chat/ChatPanelMarkdown/helpers/parseLocation';

export interface CitationNavigation {
  /** "View citation" when the link targets the document currently open, else "Open referenced document". */
  getCitationTooltip: (href: string) => string;
  /**
   * Opens a document's summary with one relationship's Related Documents card highlighted on
   * arrival, optionally landing on a citation inside it. Both halves are optional carriers:
   * a relationship's reasons may cite nothing, and a citation may belong to no relationship.
   *
   * When the citation targets the document already open there is nothing to navigate to, so it
   * scrolls to the location in place instead. Otherwise the citation rides along as
   * `params.citation` + `params.citationText` for `useCitationParamDeepLink` to pick up once the
   * destination's editor/viewer is ready.
   */
  navigateToRelationshipCard: (args: NavigateToRelationshipCardArgs) => void;
}

export interface NavigateToRelationshipCardArgs {
  /**
   * The document whose Related Documents card gets highlighted. Must be the document `citation`
   * points into when one is given — that citation is where the user lands inside it.
   */
  documentId: UUID;
  /** Jump here on arrival. Omitted when the relationship's reasons cite nothing. */
  citation?: PendingCitation;
  /** Highlighted in the destination's Related Documents on arrival. */
  relationshipId?: string | null;
}

interface PendingCitation {
  href: string;
  referenceText: string;
}

/**
 * Resolves a `#doc/<id>/ver/<id>/loc/<anchor>` citation into a scroll of the currently open
 * document. Shared by `navigateToRelationshipCard`'s already-open case and
 * `useCitationParamDeepLink`'s post-navigation follow-up, so both jump to a citation the same way.
 */
function useResolveCitation(): (href: string, referenceText: string) => void {
  const editor = useMaybeEditor();
  const { scrollToLocationForReference } = useScrollToLocationForReference();
  const dispatchLocationNotFoundNotification = useDispatchNotification({
    message:
      'Document location not found. This can occur if the document has been updated since the citation was generated.',
    variant: 'warning',
  });

  return function resolveCitation(href: string, referenceText: string): void {
    const parts = parseMarkdownLocationLink(href);
    if (!parts) return;

    const isPdfLocation = getIsPdfLocationAnchor(parts.locationId);
    const aiReference = isPdfLocation
      ? parseFileLocation(href, referenceText)
      : editor
        ? parseAlineLocation(href, editor, referenceText)
        : null;
    if (!aiReference) {
      dispatchLocationNotFoundNotification();
      return;
    }

    scrollToLocationForReference({ aiReference });
  };
}

/**
 * The navigation behind a relationship's clickable surfaces — the reasoning tooltip's inline
 * `#doc/<id>/ver/<id>/loc/<anchor>` citations (`RelationshipCitation`) and the repository's
 * relationship-row titles (`useRelationshipRowTitleClick`). Both land on the same Related
 * Documents card; a citation only decides where inside the document the user ends up.
 */
export function useCitationNavigation(): CitationNavigation {
  /**
   * Must match only `/doc/:documentId` — `useMaybeDocumentParams()` also binds on the
   * repository preview route (`/repository/documents/:documentId`), where no viewer is
   * mounted and scrolling in place would silently do nothing.
   */
  const documentId = useDocumentIdFromUrl();
  const resolveCitation = useResolveCitation();

  function getCitationTooltip(href: string): string {
    const parts = parseMarkdownLocationLink(href);
    if (!parts) return 'Open referenced document';
    return parts.documentId === documentId ? 'View citation' : 'Open referenced document';
  }

  function navigateToRelationshipCard({
    citation,
    documentId: destinationId,
    relationshipId,
  }: NavigateToRelationshipCardArgs): void {
    // Already reading the cited document — scroll to the location rather than navigating to it.
    if (citation && destinationId === documentId) {
      resolveCitation(citation.href, citation.referenceText);
      return;
    }

    const searchParams = new URLSearchParams();
    if (citation) {
      searchParams.set(params.citation, citation.href);
      searchParams.set(params.citationText, citation.referenceText);
    }
    if (relationshipId) searchParams.set(params.highlightRelationship, relationshipId);

    const search = searchParams.toString();
    const route = link.DocumentDetailSummary({ documentId: destinationId });
    /**
     * `applyRouteToExistingTab` matters when a tab for the destination is already open: tab
     * handling switches to it instead of opening a new one, and without the override the tab
     * keeps its stored route, dropping the params the arrival deep links need.
     */
    openLink(search ? `${route}?${search}` : route, { applyRouteToExistingTab: true });
  }

  return { getCitationTooltip, navigateToRelationshipCard };
}

/**
 * Consumes the citation `navigateToRelationshipCard` carries across a navigation.
 * Mirrors how `AIProperties`' questionId deep link (DocumentSummaryDrawer.tsx) and the AI-chat
 * citation deep link (`useAIChatItemParam` + `useScrollToChatItemEffect`) handle the same
 * problem: claim the one-shot from the tab's `pendingParams` (TabProvider strips ephemeral keys
 * off `tab.route`) so a refresh/tab-switch/Activity re-show cannot replay it — then, in a
 * separate effect, wait for the target's viewer (`documentViewer` for a PDF location, a fully
 * loaded `editor` for an Aline location) before actually scrolling, since the viewer/editor
 * mount well before their content finishes loading.
 *
 * Must render INSIDE `EditorRuntimeProvider` — `useMaybeDocumentViewer` (and the scroll itself)
 * read the editor runtime from context, so outside the provider the readiness gate never opens.
 */
export function CitationParamDeepLink(): null {
  useCitationParamDeepLink();
  return null;
}

/**
 * Whether the viewer has finished loading a document's CONTENT. The viewer instance itself
 * mounts long before that, and an Apryse text search against a still-loading document silently
 * does nothing — so deep-link scrolling must gate on the `documentLoaded` event (same pattern as
 * `useZoomLevel` in viewer-hooks.ts), not on the viewer's existence.
 */
function useIsViewerDocumentLoaded(): boolean {
  const documentViewer = useMaybeDocumentViewer();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!documentViewer) return;
    // The document may have finished loading before this effect attached its listener.
    if (documentViewer.getDocument()) setIsLoaded(true);

    const handleLoaded = () => setIsLoaded(true);
    const handleUnloaded = () => setIsLoaded(false);
    documentViewer.addEventListener('documentLoaded', handleLoaded);
    documentViewer.addEventListener('documentUnloaded', handleUnloaded);
    return () => {
      documentViewer.removeEventListener('documentLoaded', handleLoaded);
      documentViewer.removeEventListener('documentUnloaded', handleUnloaded);
    };
  }, [documentViewer]);

  return isLoaded;
}

function useCitationParamDeepLink(): void {
  const editor = useMaybeEditor();
  const editorLoading = useEditorLoading();
  const isViewerDocumentLoaded = useIsViewerDocumentLoaded();
  const resolveCitation = useResolveCitation();
  const resolveCitationRef = useRef(resolveCitation);
  resolveCitationRef.current = resolveCitation;
  const [citationArrival, acknowledgeCitation] = useConsumePendingParam(params.citation);
  const [citationTextArrival, acknowledgeCitationText] = useConsumePendingParam(
    params.citationText
  );
  const [pendingCitation, setPendingCitation] = useState<PendingCitation | null>(null);

  useEffect(() => {
    if (!citationArrival) return;

    acknowledgeCitation();
    acknowledgeCitationText();
    setPendingCitation({
      href: citationArrival.value,
      referenceText: citationTextArrival?.value ?? '',
    });
  }, [acknowledgeCitation, acknowledgeCitationText, citationArrival, citationTextArrival]);

  useEffect(() => {
    if (!pendingCitation) return;

    const parts = parseMarkdownLocationLink(pendingCitation.href);
    if (!parts) {
      setPendingCitation(null);
      return;
    }

    const isReady = getIsPdfLocationAnchor(parts.locationId)
      ? isViewerDocumentLoaded
      : Boolean(editor) && !editorLoading;
    if (!isReady) return;

    resolveCitationRef.current(pendingCitation.href, pendingCitation.referenceText);
    setPendingCitation(null);
  }, [pendingCitation, editor, editorLoading, isViewerDocumentLoaded]);
}
